import { useEffect } from 'react';
import { useState } from 'react';
import PropTypes from 'prop-types';
import Input from 'components/Input';
import { authPostRequest } from 'Api/api';
import { useDispatch, useSelector } from 'react-redux';
import { clearCalculationsData, setCaclulcationResults } from 'reduxStore/calculationsSlice';
import {
  getAreAllErrorsGone,
  handleInputError,
  handleKeyDown,
  isObjectEmpty,
  preventChars,
  preventCharsCal,
  preventDecimal,
  stylesInput
} from 'utils/utils';
import { clearInputValues, setInputValues } from 'reduxStore/bracketOptionsSlice';
import { DropdownNames } from 'pages/calculator/calculatorUtils';
import { Grid } from '@mui/material/index';
import { getCalcuations, handleInputValidation, inputFeildsObj } from './BracketCalcHelper';

export const InitialInputs = {
  groundSnowLoad: { value: '' },
  roofSlope: {},
  verticalExposure: { value: '' },
  horizontalExposure: { value: '' },
  rainLoad: { value: 0, isValid: true },
  numberOfRoofingSqrs: { value: '', isValid: false }
};

const ConditionalInputs = ({
  metaInputsList,
  dropDownValues,
  isEditMode,
  clearInputs,
  setDrawGraph,
  setSnowLoadImage
}) => {
  const dispatch = useDispatch();
  // inputLables: contains lables and names of only those inputs that are to be rendered
  // and is responsible for rendering list of inputs
  const { inputValues } = useSelector((store) => store.bracketOptions);
  const [inputLables, setInputLabels] = useState([metaInputsList[0]]);
  const [inputs, setInputs] = useState(InitialInputs);
  const [dropdowns, setDropdowns] = useState({});
  const [errors, setErrors] = useState({});
  const [strength, setStrength] = useState(null);
  const [outputs, setOutputs] = useState(null);
  const [renderIndex, setRenderIndex] = useState(0); // to limit rendering of inputs
  const [currentIndex, setCurrentIndex] = useState(0);
  // if focus is shifted to another input filed then it will set to true
  // untill the drop down changes
  const [snowLoadLostFocuse, setSnowLoadLostFocus] = useState(false);
  const { readOnly } = useSelector((store) => store.auth);

  useEffect(() => {
    if (isEditMode) {
      const inputsObject = {};
      for (const key in inputValues) {
        inputsObject[key] = { value: inputValues[key]?.value, isValid: true };
      }
      setInputs(inputsObject);
      setRenderIndex(inputValues?.length || 0);
      setInputLabels(metaInputsList);
    }
  }, []);

  useEffect(() => {
    if (clearInputs) {
      setInputs(InitialInputs);
      setSnowLoadLostFocus(false);
      setInputLabels([metaInputsList[0]]);
      setRenderIndex(0);
      dispatch(clearInputValues());
    }
  }, [dropDownValues]);

  useEffect(() => {
    // eidt mode logic...
    if (!isEditMode || clearInputs) {
      metaInputsList.map((input, index) => {
        // true when previous input has valid value init amd index is above render Index
        if (index > renderIndex && inputs[metaInputsList[index - 1]?.name]?.isValid) {
          setInputLabels((prevState) => [...prevState, input]);
          setRenderIndex((prevIndex) => prevIndex + 1);
        }
      });
    }
  }, [inputs]);

  const handleError = (input, errorMessage) => {
    handleInputError(input, errorMessage, setErrors);
  };

  useEffect(() => {
    if (inputLables[currentIndex].name == 'groundSnowLoad' && !snowLoadLostFocuse && !isEditMode) {
      setSnowLoadImage(true);
    }
  }, [currentIndex]);

  const handleInputChange = (e, min, max) => {
    // check if the input has valid value or not
    const isNotValid = handleInputValidation(e.target.name, e.target.value, min, max, handleError);
    const index = inputLables.findIndex((val) => val.name === e.target.name);
    if (index !== currentIndex) {
      setCurrentIndex(index);
    }
    // update state value of field
    setInputs((prevState) => ({
      ...prevState,
      [e.target.name]: { value: e.target.value, isValid: !isNotValid }
    }));
    dispatch(setInputValues({ name: e.target.name, value: e.target.value }));
    // clear error if has valid value
    if (!isNotValid) handleError(e.target.name, null);
  };

  // if no iput is available then render nothing
  if (!metaInputsList?.length) return null;

  const getBrktStrength = async (stringForApi) => {
    try {
      const strength = await authPostRequest('findStrength', {
        combination: stringForApi
      });
      setStrength(Number(strength?.strengthValue));
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (outputs) {
      dispatch(setCaclulcationResults(outputs));
    }
    return () => dispatch(clearCalculationsData());
  }, [outputs, strength]);

  // calculate results
  useEffect(() => {
    if (strength && getAreAllErrorsGone(errors) && inputs.numberOfRoofingSqrs.isValid) {
      getCalcuations(dropdowns, inputs, strength, setOutputs);
    } else {
      setDrawGraph(false);
      setOutputs(null);
    }
  }, [strength, inputs, errors]);

  useEffect(() => {
    if (!isObjectEmpty(outputs)) setSnowLoadImage(false);
  }, [outputs]);

  // setup states like dropdowns, bracket strength
  useEffect(() => {
    // array of dropdown values
    const dropdownsArr = [...dropDownValues]?.map(([_, value]) => {
      return {
        value: value.value,
        name: value.name
      };
    });
    // this object will contain dropdown name and value pairs
    const dropdownsObj = {};
    for (const dropdown of dropdownsArr) {
      dropdownsObj[dropdown.name] = dropdown.value;
    }
    setDropdowns(dropdownsObj);

    const initialApiString = `${dropdownsObj[DropdownNames.snowBracketType]}@${
      dropdownsObj[DropdownNames.snowBracketMaterial]
    }@${dropdownsObj[DropdownNames.roofSheathingType] || ''}@${
      dropdownsObj[DropdownNames.fastenerType] || ''
    }`;

    const trimedString = initialApiString.split('@');
    const Apistring = trimedString.filter((item) => item.trim()).join(' ');
    getBrktStrength(Apistring);
  }, [dropDownValues]);

  return (
    <Grid sx={{ mt: 2 }}>
      {inputLables.map((input) => {
        return (
          <Input
            type="number"
            key={input.label}
            sx={{ mt: 1, mx: 0 }}
            maxLength={inputFeildsObj[input.label]?.maxLength}
            onChange={(e) =>
              handleInputChange(
                e,
                inputFeildsObj[input.label]?.min,
                inputFeildsObj[input.label]?.max
              )
            }
            disabled={readOnly}
            onKeyDown={(event) => {
              (inputFeildsObj[input.label].name == 'groundSnowLoad' ||
                inputFeildsObj[input.label].name == 'roofSlope' ||
                inputFeildsObj[input.label].name == 'verticalExposure' ||
                inputFeildsObj[input.label].name == 'horizontalExposure') &&
                preventCharsCal(event);
              inputFeildsObj[input.label].name == 'numberOfRoofingSqrs' && preventDecimal(event);
              preventChars(event);
              handleKeyDown(event);
            }}
            onFocus={() => {
              if (inputFeildsObj[input.label].name == 'groundSnowLoad' && isObjectEmpty(outputs))
                setSnowLoadImage(true);
              else if (inputFeildsObj[input.label].name != 'groundSnowLoad') {
                setSnowLoadImage(false);
                setSnowLoadLostFocus(true);
              }
            }}
            styleInput={stylesInput}
            value={inputs[input.name]?.value}
            required={true}
            label={inputFeildsObj[input.label].label}
            description={
              inputFeildsObj[input.label].name == 'groundSnowLoad'
                ? 'Find Ground Snow Load at'
                : false
            }
            description2={
              inputFeildsObj[input.label].name == 'groundSnowLoad'
                ? 'UTAH ONLY - Find Ground Snow Load at'
                : false
            }
            name={inputFeildsObj[input.label].name}
            error={errors[inputFeildsObj[input.label].name]}
          />
        );
      })}
    </Grid>
  );
};

export default ConditionalInputs;

ConditionalInputs.propTypes = {
  metaInputsList: PropTypes.array,
  dropDownValues: PropTypes.map,
  isEditMode: PropTypes.bool,
  clearInputs: PropTypes.func,
  setDrawGraph: PropTypes.func,
  setSnowLoadImage: PropTypes.func
};
